import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import BusinessUserForm from "./BusinessUserForm";
import BusinessUserList from "./BusinessUserList";
import BusinessUserDetail from "./BusinessUserDetail";
import { BusinessUser } from "../../CRUD/CampSites/types";


const BusinessUserManager: React.FC = () => {
  const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingBusinessUser, setEditingBusinessUser] = useState<BusinessUser | null>(null);
  const [currentView, setCurrentView] = useState<"list" | "form" | "details">("list");
  const [selectedBusinessUser, setSelectedBusinessUser] = useState<BusinessUser | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem('accessToken');

  const navigate = useNavigate();

  const fetchBusinessUsers = async () => {
    try {
      const response = await axios.get<{ data: BusinessUser[] }>(`${apiUrl}/BusinessUser/getAllBusinessUsers`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setBusinessUsers(response.data.data || []);
    } catch (error) {
      console.error('Error fetching business users', error);
    }
  };

  useEffect(() => {
    fetchBusinessUsers();
  }, []);

  const handleAddBusinessUser = async (data: BusinessUser) => {
    await fetchBusinessUsers(); // Refresh the list after adding
    setShowForm(false);
    setCurrentView("list");
  };

  const handleUpdateBusinessUser = async (businessUser: BusinessUser) => {
    await fetchBusinessUsers(); // Refresh the list after updating
    setShowForm(false);
    setCurrentView("list");
  };

  const handleCancelEdit = () => {
    setShowForm(false);
    setEditingBusinessUser(null);
    setCurrentView("list");
  };

  const handleViewBusinessUser = (businessUser: BusinessUser) => {
    setSelectedBusinessUser(businessUser);
    setCurrentView("details");
  };

  const handleDeleteBusinessUser = async (id: number) => {
    try {
      await axios.delete(`${apiUrl}/BusinessUser/deleteBusinessUser/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      await fetchBusinessUsers(); // Refresh the list after deleting
    } catch (error) {
      console.error('Error deleting business user', error);
    }
  };

  const handleSuccess = () => {
    fetchBusinessUsers(); // Refresh the list after success
    setShowForm(false);
    setEditingBusinessUser(null);
    setCurrentView("list");
  };

  const handleEditBusinessUser = (businessUser: BusinessUser) => {
    setEditingBusinessUser(businessUser);
    setShowForm(true);
    setCurrentView("form");
  };


  return (
    <div className="container mb-5 pb-5">
      {/* <h1 className="text-center mb-4">Business User Manager</h1> */}
      <div className="d-flex justify-content-center mb-4">
        {currentView === "list" && (
          <button className="btn btn-primary" onClick={() => { setShowForm(true); setCurrentView("form"); }}>
            Add Business User
          </button>
        )}
      </div>
      {currentView === "list" && (
        <BusinessUserList
          businessUsers={businessUsers}
          onEdit={handleEditBusinessUser}
          onDelete={handleDeleteBusinessUser}
          onView={handleViewBusinessUser}
        />
      )}
      {currentView === "form" && (
        <BusinessUserForm
          businessUserId={editingBusinessUser?.id} // Pass businessUserId prop
          onSubmit={editingBusinessUser ? handleUpdateBusinessUser : handleAddBusinessUser}
          onCancel={handleCancelEdit}
          onEdit={editingBusinessUser ? handleEditBusinessUser : () => {}}
          initialCompanyName={editingBusinessUser?.companyName}
          initialName={editingBusinessUser?.name}
          initialEmail={editingBusinessUser?.email}
          initialPhoneNumber={editingBusinessUser?.phoneNumber}
          initialAddress={editingBusinessUser?.address}
          initialAddress2={editingBusinessUser?.address2}
          initialCity={editingBusinessUser?.city}
          initialZipCode={editingBusinessUser?.zipCode}
          initialCountry={editingBusinessUser?.country}
          initialComments={editingBusinessUser?.comments}
          onSuccess={handleSuccess}
          isEditing={!!editingBusinessUser} // Pass isEditing prop
        />
      )}
      {currentView === "details" && selectedBusinessUser && (
        <BusinessUserDetail
          businessUser={selectedBusinessUser}
          onClose={handleCancelEdit}
          onEdit={handleEditBusinessUser}
        />
      )}
    </div>
  );
};

export default BusinessUserManager;